import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Camera, UploadSimple, ArrowClockwise } from "@phosphor-icons/react";

export default function PhotoExtractor({ onExtracted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [raw, setRaw] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setRaw(null);
  };

  const reset = () => { setFile(null); setPreview(null); setRaw(null); };

  const process = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/assets/parse-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setRaw(res.data.extracted);
      onExtracted?.(res.data.extracted || {});
    } catch (e) {
      toast.error("Extraction failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="photo-extractor" className="space-y-6">
      <div>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
          Visual Extraction · OCR & Vision
        </div>
        <h2 className="font-serif-display text-3xl text-[#0A192F]">Photograph the plate. We read it.</h2>
        <p className="text-sm text-[#5C5F58] mt-3 max-w-2xl">
          Model numbers, serial stickers, maker&apos;s marks, dataplate labels. The camera captures the detail you used to crawl behind appliances to find. GPT-5.2 Vision extracts the text into structured fields.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#F9F8F6] border border-[#E0DDD6] aspect-[4/3] flex items-center justify-center overflow-hidden">
          {preview ? (
            <img src={preview} alt="Preview" className="max-w-full max-h-full object-contain" data-testid="photo-preview" />
          ) : (
            <div className="text-center p-6">
              <Camera size={48} color="#5C5F58" className="mx-auto mb-3" />
              <p className="text-sm text-[#5C5F58]">No image selected</p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <label className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1A3622] text-[#F9F8F6] text-sm cursor-pointer hover:bg-[#0F2818] border border-[#0F2818]">
            <UploadSimple size={16} /> {file ? "Choose different image" : "Choose photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" data-testid="photo-upload" />
          </label>
          {file && (
            <>
              <button
                onClick={process}
                disabled={processing}
                data-testid="extract-image"
                className="px-5 py-3 text-sm bg-white border border-[#1A3622] text-[#1A3622] hover:bg-[#1A3622] hover:text-[#F9F8F6] disabled:opacity-60"
              >
                {processing ? "Reading label…" : "Extract Details"}
              </button>
              <button onClick={reset} className="px-5 py-3 text-sm border border-[#E0DDD6] bg-white inline-flex items-center justify-center gap-2">
                <ArrowClockwise size={14} /> Reset
              </button>
            </>
          )}
          {raw && (
            <div className="bg-white border border-[#E0DDD6] p-4" data-testid="extracted-data">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                Extracted
              </div>
              <dl className="space-y-1 text-xs font-mono-ledger">
                {["brand", "model", "serial", "category"].map((k) =>
                  raw[k] ? (
                    <div key={k} className="flex justify-between gap-4">
                      <dt className="text-[#5C5F58] uppercase tracking-wider">{k}</dt>
                      <dd className="text-[#0A192F]">{raw[k]}</dd>
                    </div>
                  ) : null
                )}
              </dl>
              <p className="text-xs text-[#5C5F58] mt-3 italic">Draft sent to form.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
